import { BUSINESS_ID, PERSON_ID, WEBSITE_ID, wordCount } from "@/lib/structured-data/entity-graph";
import { articleTitle } from "@/lib/seo-titles";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import {
  getPostBySlug,
  getPostTitleBySlug,
  listLivePostSlugs,
} from "@/lib/public.functions";
import { ArticleBody, useArticleBody } from "@/components/public/prose";
import { ArticleToc } from "@/components/public/article-toc";
import { BlogShare } from "@/components/public/blog-share";
import { PostFaq, type FaqEntry } from "@/components/public/post-faq";
import { useHeaderEndSlot, useHeaderTone } from "@/components/public/header-slot";
import { ArticleActions } from "@/components/blog/article-actions";
import { Section } from "@/components/ds/section";
import { BrownCtaBand, PillarPill, eyebrow, postMeta, type BlogPost } from "@/components/blog/shared";
import { NextInPathCard } from "@/components/blog/path-view";
import { blogPostsQ, blogServicesQ, consultPrices } from "@/features/blog/queries";
import { useReader } from "@/features/blog/use-reader";
import { chipById, ltr } from "@/lib/blog/age-bands";
import { orderPath } from "@/lib/blog/paths";
import { primaryPillar } from "@/lib/blog/pillars";
import { minutesPhrase } from "@/lib/blog/text";
import { nextStepFor } from "@/data/article-next-step";
import { fillDuration, ageRange } from "@/lib/home-format";
import avatarAsset from "@/assets/dr-sara-headshot.jpg.asset.json";
import { absoluteUrl, canonicalLink, canonicalMeta, ogImageMeta, DEFAULT_OG_IMAGE } from "@/lib/seo";
import { arabicDate, toLatinDigits } from "@/lib/arabic-format";

const SITE_NAME = "د. سارة النحّاس";

const postQ = (slug: string) =>
  queryOptions({ queryKey: ["public", "post", slug], queryFn: () => getPostBySlug({ data: { slug } }) });
const pillarQ = (slug: string) =>
  queryOptions({ queryKey: ["public", "pillar", slug], queryFn: () => getPostTitleBySlug({ data: { slug } }) });

function parseFaq(raw: unknown): FaqEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => (e && typeof e === "object" ? (e as Record<string, unknown>) : null))
    .filter(Boolean)
    .map((e) => ({ q: String(e!["q"] ?? ""), a: String(e!["a"] ?? "") }))
    .filter((e) => e.q && e.a);
}

// Dates follow src/lib/arabic-format.ts: Latin numerals, Levantine month names.


/** One spelling of her name everywhere, whatever a legacy row happens to store. */
function canonicalAuthor(name?: string | null): string {
  return (name ?? SITE_NAME).replace("النحاس", "النحّاس");
}

function laterOf(a?: string, b?: string): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) > new Date(b) ? a : b;
}

function isoOrUndefined(value?: string | null): string | undefined {
  return value ? new Date(value).toISOString() : undefined;
}

export const Route = createFileRoute("/_public/blog/$slug")({
  loader: async ({ context, params }) => {
    const post = await context.queryClient.ensureQueryData(postQ(params.slug));
    if (!post) throw notFound();
    // Returned in loader data (not only the query cache) so server and first
    // browser render agree on which in-body blog links are live — see AGENTS.md.
    const liveSlugs = await listLivePostSlugs();
    await Promise.all([
      context.queryClient.ensureQueryData(blogPostsQ()),
      context.queryClient.ensureQueryData(blogServicesQ()),
    ]);
    if (post.pillar_slug) context.queryClient.ensureQueryData(pillarQ(post.pillar_slug));
    return {
      title: post.title_ar,
      seoTitle: post.seo_title ?? null,
      wordCount: wordCount(post.body_md),
      description: post.meta_description ?? post.excerpt_ar ?? "",
      image: post.og_image ?? post.cover_image ?? null,
      author: canonicalAuthor(post.author),
      authorTitle: post.author_title,
      publishedAt: isoOrUndefined(post.published_at),
      updatedAt: isoOrUndefined(post.updated_at),
      // Scheduled posts are edited before they go live, so `updated_at` can
      // precede `published_at`. Schema requires dateModified >= datePublished.
      modifiedAt: laterOf(isoOrUndefined(post.updated_at), isoOrUndefined(post.published_at)),
      tags: post.tags ?? [],
      primaryKeyword: post.primary_keyword ?? null,
      faq: parseFaq(post.faq),
      liveSlugs,
    };
  },
  head: ({ params, loaderData }) => {
    const path = `/blog/${params.slug}`;
    const url = absoluteUrl(path);
    if (!loaderData) return { meta: [{ title: `مقال — ${SITE_NAME}` }] };
    const image = loaderData.image ? absoluteUrl(loaderData.image.replace(/^https?:\/\/[^/]+/, "")) : null;
    const imageAbsolute = loaderData.image?.startsWith("http") ? loaderData.image : image;
    const keywords = [...loaderData.tags, loaderData.primaryKeyword].filter(Boolean).join(", ");

    const articleLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: loaderData.title,
      description: loaderData.description || undefined,
      image: imageAbsolute ?? undefined,
      inLanguage: "ar",
      datePublished: loaderData.publishedAt,
      dateModified: loaderData.modifiedAt,
      author: { "@id": PERSON_ID, "@type": "Person", name: "د. سارة النحّاس", url: "https://drsaranahas.com/about" },
      publisher: {
        "@id": BUSINESS_ID,
        "@type": "Organization",
        name: "د. سارة النحّاس",
        logo: { "@type": "ImageObject", url: "https://drsaranahas.com/logo.png" },
      },
      isPartOf: { "@id": WEBSITE_ID },
      wordCount: loaderData.wordCount,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      keywords: keywords || undefined,
      articleSection: loaderData.tags[0] || undefined,
    };

    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "الرئيسية", item: absoluteUrl("/") },
        { "@type": "ListItem", position: 2, name: "المدوّنة", item: absoluteUrl("/blog") },
        { "@type": "ListItem", position: 3, name: loaderData.title, item: url },
      ],
    };

    const faqLd =
      loaderData.faq.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            inLanguage: "ar",
            mainEntity: loaderData.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null;

    return {
      meta: [
        { title: articleTitle(loaderData.seoTitle, loaderData.title) },
        { name: "description", content: loaderData.description },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: loaderData.description },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "ar_SY" },
        { property: "og:site_name", content: SITE_NAME },
        ...ogImageMeta(imageAbsolute ?? DEFAULT_OG_IMAGE),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: loaderData.title },
        { name: "twitter:description", content: loaderData.description },
        ...(loaderData.publishedAt
          ? [{ property: "article:published_time", content: loaderData.publishedAt }]
          : []),
        ...(loaderData.modifiedAt
          ? [{ property: "article:modified_time", content: loaderData.modifiedAt }]
          : []),
        { property: "article:author", content: loaderData.author },
        ...canonicalMeta(path),
      ],
      links: [canonicalLink(path)],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(articleLd) },
        ...(faqLd ? [{ type: "application/ld+json", children: JSON.stringify(faqLd) }] : []),
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-foreground">لم نجد هذا المقال</h1>
      <Link to="/blog" className="mt-6 inline-block font-semibold text-amber-800 underline">
        عودة إلى المدوّنة
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => <p className="mx-auto max-w-3xl p-10 text-brown-500">{String(error)}</p>,
  component: Page,
});


/** Scroll position through the article body, 0..1. */
function useBodyProgress(ref: React.RefObject<HTMLElement | null>) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const span = rect.height - window.innerHeight * 0.6;
      const passed = -rect.top + window.innerHeight * 0.4;
      setProgress(span > 0 ? Math.min(1, Math.max(0, passed / span)) : 1);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref]);
  return progress;
}

function Page() {
  const { slug } = Route.useParams();
  const { data: post } = useSuspenseQuery(postQ(slug));
  const { data: list } = useSuspenseQuery(blogPostsQ());
  const { data: services } = useSuspenseQuery(blogServicesQ());
  const { liveSlugs } = Route.useLoaderData();
  const body = useArticleBody(post?.body_md ?? "", post?.mark_phrases ?? [], liveSlugs);
  const { reader, isSaved, isRead, toggleSave, markRead } = useReader();
  const ageNavRef = useRef<HTMLElement>(null);
  const [relIdx, setRelIdx] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const progress = useBodyProgress(bodyRef);
  const posts = list.rows as unknown as BlogPost[];
  const postId = post?.id ?? "";
  const saved = postId ? isSaved(postId) : false;

  useHeaderTone("article");
  useHeaderEndSlot(
    post ? (
      <ArticleActions
        readingMin={post.reading_min}
        progress={progress}
        saved={saved}
        onToggleSave={() => void toggleSave(post.id)}
        title={post.title_ar}
      />
    ) : undefined,
    `${slug}-${saved}-${Math.round(progress * 40)}`,
  );

  useEffect(() => {
    const nav = ageNavRef.current;
    const header = document.querySelector("header");
    if (!nav || !header) return;
    const updateOffset = () => {
      nav.style.setProperty("--article-header-height", `${header.getBoundingClientRect().height}px`);
    };
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(header);
    return () => observer.disconnect();
  }, [body.headings, post?.age_band]);

  // Read once the end of the body scrolls into view.
  useEffect(() => {
    const el = endRef.current;
    if (!el || !postId) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        markRead(postId);
        io.disconnect();
      }
    });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, reader.mode]);

  const pillar = primaryPillar(post?.rakiza);
  const related = useMemo(() => {
    if (!post) return [];
    const byId = new Map(posts.map((p) => [p.id, p]));
    const curated = (post.related_post_ids ?? []).map((id: string) => byId.get(id)).filter(Boolean) as BlogPost[];
    const same = posts.filter((p) => primaryPillar(p.rakiza) === pillar || (p.rakiza ?? []).includes(pillar));
    const out: BlogPost[] = [];
    for (const p of [...curated, ...same]) {
      if (p.id !== post.id && !out.includes(p)) out.push(p);
      if (out.length === 6) break;
    }
    return out;
  }, [post, posts, pillar]);

  const pathInfo = useMemo(() => {
    if (!reader.age || !post) return null;
    const path = orderPath(reader.age, posts);
    if (!path.some((p) => p.id === post.id)) return null;
    const next = path.find((p) => p.id !== post.id && !isRead(p.id));
    return next ? { next, index: path.indexOf(next) + 1, total: path.length } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reader, post, posts]);

  if (!post) return null;

  const faq = parseFaq(post.faq);
  const ageHeadings = body.headings.filter((heading) => heading.text.startsWith("من عمر"));
  const ageBands = post.age_band ?? [];
  const prices = consultPrices(services as any[]);
  const nextStep = nextStepFor(slug);
  const age = ageRange(post.age_band);
  const topMeta = [post.reading_min ? `${minutesPhrase(post.reading_min)} قراءة` : null, age ? `${ltr(age)} سنوات` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="bg-page-soft">
      <article>
        <Section tone="white" edge="hairline" inner="mx-auto w-full min-w-0 max-w-[44rem] px-5 pb-10 pt-8 md:pb-12 md:pt-12">
          <nav aria-label="مسار التنقّل" className="text-[13px] text-text-secondary">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to="/" className="inline-flex min-h-11 items-center hover:text-maroon-500">الرئيسية</Link>
              </li>
              <li aria-hidden>/</li>
              <li>
                <Link to="/blog" className="inline-flex min-h-11 items-center hover:text-maroon-500">المدوّنة</Link>
              </li>
            </ol>
          </nav>

          <PillarPill rakiza={post.rakiza} className="mt-6" />
          {topMeta && <p className="mt-3 text-[14px] text-text-secondary">{topMeta}</p>}

          <h1 className="mt-3 font-display text-[34px] font-extrabold leading-[1.22] text-text-primary md:text-[42px]">
            {post.title_ar}
          </h1>

          <Link to="/about" className="mt-5 inline-flex min-h-11 items-center gap-2.5 text-[14px] text-text-secondary">
            <img src={avatarAsset.url} alt="د. سارة النحّاس" width={400} height={400} loading="lazy" decoding="async" className="h-9 w-9 rounded-full object-cover object-center" />
            <span>
              <span className="font-display font-bold text-text-primary">{canonicalAuthor(post.author)}</span>
              {post.published_at && <> · {arabicDate(post.published_at)}</>}
            </span>
          </Link>

          {post.cover_image && (
            <img
              src={post.cover_image}
              alt={post.image_alt || ""}
              width={1200}
              height={630}
              className="mt-6 aspect-[1200/630] w-full rounded-ds-2xl object-cover"
            />
          )}
        </Section>

        <Section tone="soft" inner="mx-auto w-full min-w-0 max-w-[44rem] px-5 pt-4">

          {post.tldr_ar && (
            <section aria-label="خلاصة القول" className="mt-8 rounded-ds-2xl bg-blue-100 p-6">
              <p className="font-display text-[13px] font-bold text-blue-700">خلاصة القول</p>
              <p className="mt-2 text-[16px] leading-[1.8] text-text-primary">{post.tldr_ar}</p>
            </section>
          )}

          <ArticleToc headings={body.headings} />

          <div>
            {ageHeadings.length >= 3 && ageBands.length === ageHeadings.length && (
              <nav
                ref={ageNavRef}
                aria-label="الانتقال إلى عمر طفلك"
                className="article-age-jumps sticky top-[var(--article-header-height,4rem)] z-30 mt-6 flex snap-x snap-mandatory gap-2 overflow-x-auto no-scrollbar bg-bg-page-soft py-3 lg:static"
              >
                {ageHeadings.map((heading, i) => (
                  <a
                    key={heading.id}
                    href={`#${heading.id}`}
                    aria-label={heading.text}
                    className="inline-flex min-h-11 shrink-0 snap-start items-center rounded-ds-pill bg-amber-100 px-4 font-display text-[14px] font-bold text-amber-800 transition-colors duration-ds-fast hover:bg-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                  >
                    {toLatinDigits(ageBands[i])}
                  </a>
                ))}
              </nav>
            )}

            <div ref={bodyRef} className="mt-8 rounded-ds-xl border border-hairline-subtle bg-surface p-6 shadow-sm md:p-10">
              <ArticleBody html={body.html} />
              <div ref={endRef} aria-hidden className="h-px" />
              <div className="mt-10 border-t border-hairline-subtle pt-6">
                <BlogShare title={post.title_ar} slug={slug} />
              </div>
            </div>
          </div>
        </Section>
      </article>

      <Section tone="soft" as="div" inner="mx-auto flex max-w-6xl flex-col items-center px-5 pb-14 md:pb-20">
        <div className="w-full max-w-[44rem]">
          <div id="cta-capture" className="scroll-mt-24 [&>section]:mx-0">
            <BrownCtaBand
              eyebrowText="الخطوة الجاية"
              heading={nextStep.heading}
              line={fillDuration(nextStep.line, prices.duration ?? 45)}
              price={prices.price}
              packagePrice={prices.packagePrice}
              headingSize="text-[28px]"
            />
          </div>

          {pathInfo && reader.age && (
            <>
              <p className={`${eyebrow} mt-10`}>بمسارك · {ltr(chipById(reader.age).label)} سنوات</p>
              <div className="[&>section]:mt-3">
                <NextInPathCard post={pathInfo.next} index={pathInfo.index} total={pathInfo.total} />
              </div>
            </>
          )}

          <PostFaq items={faq} />

          {post.boundary_note && (
            <p className="mt-10 text-[14px] leading-[1.75] text-text-secondary">
              هذا المقال محتوى تثقيفي عام، وليس تشخيصاً ولا بديلاً عن التقييم المتخصّص. إذا لاحظتوا أنّ الصعوبة
              مستمرّة، أو تزداد، أو تؤثّر على نوم طفلك أو دراسته أو علاقاته، فالخطوة الصحيحة طلب استشارة مختصّ في نفس
              الطفل أو طبيب الأطفال.
            </p>
          )}

          <section className="reveal mt-12 flex flex-col items-center gap-5 rounded-ds-xl bg-cream-50 p-7 text-center md:flex-row md:text-start">
            <img src={avatarAsset.url} alt="د. سارة النحّاس" width={400} height={400} loading="lazy" decoding="async" className="h-24 w-24 shrink-0 rounded-ds-lg object-cover object-center" />
            <div className="min-w-0">
              <h2 className="font-display text-[23px] font-extrabold text-text-primary">{canonicalAuthor(post.author)}</h2>
              {post.author_title && <p className="mt-2 leading-[1.8] text-text-secondary">{post.author_title}</p>}
              <Link to="/about" className="mt-3 inline-flex min-h-11 items-center font-display font-bold text-text-link underline underline-offset-4">
                التعرّف أكثر على د. سارة
              </Link>
            </div>
          </section>

          {post.pillar_slug && <PillarLink slug={post.pillar_slug} />}
        </div>
      </Section>

      {related.length > 0 && (
        <Section tone="white" inner="mx-auto flex max-w-6xl flex-col items-center px-5 py-14">
          <div className="reveal w-full max-w-[44rem] lg:max-w-[64rem]">
            <h2 className="font-display text-[23px] font-extrabold text-text-primary">من نفس الركيزة · {pillar}</h2>
            <div onScroll={(e) => { const el = e.currentTarget; const card = el.firstElementChild as HTMLElement | null; const step = card ? card.offsetWidth + 12 : 252; setRelIdx(Math.min(related.length - 1, Math.round(Math.abs(el.scrollLeft) / step))); }} className="-mx-5 mt-4 flex snap-x snap-mandatory scroll-px-5 gap-3 overflow-x-auto no-scrollbar px-5 pb-2 lg:mx-0 lg:grid lg:grid-cols-3 lg:overflow-visible lg:px-0">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to="/blog/$slug"
                  params={{ slug: r.slug }}
                  className="lift w-[240px] shrink-0 snap-start overflow-hidden rounded-ds-xl border border-hairline-subtle bg-surface shadow-sm lg:w-auto"
                >
                  {r.og_image && (
                    <img src={r.og_image} alt={r.title_ar} width={1200} height={630} loading="lazy" className="aspect-[1200/630] w-full object-cover" />
                  )}
                  <div className="p-4">
                    <h3 className="font-display text-[15px] font-bold leading-snug text-text-primary">{r.title_ar}</h3>
                    <p className="mt-2 text-[13px] text-text-secondary">{postMeta(r)}</p>
                  </div>
                </Link>
              ))}
            </div>
            {related.length > 1 && (
              <div className="mt-3 flex justify-center gap-1.5 lg:hidden" aria-hidden>
                {related.map((r, i) => (
                  <span key={r.id} className={`h-2 rounded-ds-pill transition-all duration-ds-base motion-reduce:transition-none ${i === relIdx ? "w-5 bg-amber-800" : "w-2 bg-cream-400"}`} />
                ))}
              </div>
            )}
          </div>
        </Section>
      )}
    </div>
  );
}

function PillarLink({ slug }: { slug: string }) {
  const { data } = useSuspenseQuery(pillarQ(slug));
  if (!data) return null;
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: data.slug }}
      className="lift mt-6 block rounded-ds-xl border border-hairline-subtle bg-surface p-5 shadow-sm"
    >
      <span className="block font-display text-[13px] font-bold text-amber-800">المقال الشامل</span>
      <span className="mt-1 block font-display font-bold text-text-primary">{data.title_ar}</span>
    </Link>
  );
}
